import { makeAutoObservable, observable, runInAction } from "mobx";
import { flattenPageFields } from "@/components/intake/workflow/page-fields";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { workflowSaveChanges } from "@/lib/workflow-analytics";
import {
  resolveWorkflowDrop,
  resolveWorkflowLaneMove,
  type TWorkflowDropSlot,
  type TWorkflowDropTarget,
} from "@/lib/intake/workflow-drop-slot";
import { isOpenLinearWorkflowTailKind } from "@/lib/intake/workflow-stage-kind";
import {
  deleteKeepingLane,
  laneSubtreeIds,
  moveLaneSteps,
  outcomeLaneKeys,
  workflowSubtreeIds,
} from "@/lib/intake/workflow-step-deletion";
import type { RootStore } from "./root.store";
import { newWorkflowId, toSavePayload } from "./workflow-save-payload";
import type { TIntakePage } from "@/types/intake";
import type {
  TFormWorkflow,
  TPublishIncompleteNode,
  TWorkflowAvailableField,
  TWorkflowBranchCondition,
  TWorkflowBranchLogic,
  TWorkflowCompletion,
  TWorkflowDecisionOption,
  TWorkflowElement,
  TWorkflowElementKind,
  TWorkflowLookups,
  TWorkflowPageField,
  TWorkflowPreviewStep,
  TWorkflowStatusSource,
  TWorkflowValidationWarning,
} from "@/types/form-workflow";

/**
 * Single-form editing surface for the Form Workflow Builder (Epic #2086,
 * persistence #2116). One form is open at a time on the forms-edit route, so
 * state is flat: the loaded ``workflow`` envelope, a working ``elements[]`` copy the
 * canvas adapter reads, and the ``lookups``.
 *
 * The workflow OWNS its page content (one source of truth, #2163): structural
 * edits mutate ``elements[]`` and bump ``dirtyVersion`` so the autosave effect
 * (in ``FormWorkflowEditorProvider``) PUTs the whole document. The PUT response
 * is authoritative — every successful save re-seats from it to adopt
 * BE-normalized positions, refreshed warnings, and the seeded first-Page id —
 * except for edits that landed while the PUT was in flight (the intake
 * letter-drop save-race; see ``save``).
 *
 * Per CLAUDE.md every catch logs with context (the request-workflow store this
 * mirrors does NOT log in its ``finally`` blocks — we add it here).
 */
export class FormWorkflowStore {
  workflow: TFormWorkflow | null = null;
  /** Working copy the graph adapter reads; round-trips nested elements untouched. */
  elements: TWorkflowElement[] = [];
  lookups: TWorkflowLookups | null = null;

  isLoading = false;
  isSaving = false;
  loadError: string | null = null;
  /** HTTP status the failed draft GET came back with, when the rejection carried
   *  one (a network/parse failure carries none). Kept beside ``loadError`` so the
   *  builder's error banner can name it — "403" is the difference between "this
   *  isn't yours" and "something broke", and a reader who can see the Workflow tab
   *  but not the workflow behind it is exactly the case NG-409 made reachable. */
  loadErrorStatus: number | null = null;

  lastSavedIso: string | null = null;
  hasUnpublishedChanges = false;

  /** Monotonic edit counter — the autosave effect depends on this primitive so
   *  each edit resets its debounce timer. */
  dirtyVersion = 0;
  isDirty = false;

  /** Parsed from a 422 publish response (banner is #2095). */
  incompleteNodes: TPublishIncompleteNode[] = [];

  /**
   * Counts author edits to the graph. Distinct from {@link dirtyVersion}, which
   * drives the autosave debounce and must not be bumped by a save that has
   * already persisted.
   *
   * The point is that a *server* reseating ``elements`` does not count. An
   * earlier version of this stamped the ``elements`` array itself and compared
   * identity, which read as an edit whenever an autosave echo or the publish
   * response seated a fresh array — so the banner went stale the instant it
   * appeared, before the author touched anything. That reproduced only against a
   * real deployment, where the echo lands after the publish rather than before.
   */
  private graphVersion = 0;

  /**
   * {@link graphVersion} at the moment the current {@link incompleteNodes} was
   * requested.
   *
   * Initialising to 0 is safe only because the 422 handler in
   * {@link publishWorkflow} is the sole writer of a non-empty
   * ``incompleteNodes`` and stamps this in the same action — so a report on
   * screen always has a stamp. A second writer that forgets to stamp would read
   * as current forever; ``reset()`` needs no counterpart because it clears the
   * list, and the getter is guarded on the list being non-empty.
   */
  private incompleteNodesAtGraphVersion = 0;

  /**
   * True once the AUTHOR edited the graph since {@link incompleteNodes} was
   * computed (a server reseat deliberately does not count), so
   * the banner can stop asserting those blockers are still current (NG-859).
   *
   * Staleness, not re-validation: only the backend validates, so the client can
   * never know which blocker a given edit fixed. The list is kept — it is the
   * author's to-do list for this publish attempt — and only the claim that it is
   * current is withdrawn. Derived rather than stored so it cannot disagree with
   * the list: an empty report is never stale, by construction.
   */
  get incompleteNodesStale(): boolean {
    return (
      this.incompleteNodes.length > 0 && this.graphVersion !== this.incompleteNodesAtGraphVersion
    );
  }

  private rootStore: RootStore;

  /** The most recent in-flight ``save`` promise, or ``null`` when idle. Lets
   *  {@link flushPendingSave} await a debounced / in-flight autosave before a
   *  read that must reflect the latest edit (the builder Preview, #2526). Not
   *  observable — it is control state, never rendered. */
  private saveInFlight: Promise<void> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        saveInFlight: false,
        elements: observable,
        incompleteNodes: observable,
      } as never,
      { autoBind: true },
    );
  }

  /** True when there is a published version to revert the draft graph to, i.e.
   *  when Discard has something to do (NG-211).
   *
   *  Not the same as {@link hasUnpublishedChanges}: a workflow that has never
   *  been published reports that as true — everything in it is unpublished —
   *  but there is nowhere to go back to, which the BE answers with a 409. The
   *  builder disables Discard for that case rather than offering an action that
   *  can only fail. */
  get canRevertToPublished(): boolean {
    return Boolean(this.workflow?.published_at) && this.hasUnpublishedChanges;
  }

  /** True when the workflow can be started over rather than reverted (NG-235).
   *
   *  The complement of {@link canRevertToPublished} on the published axis: a
   *  workflow that has never been live has nothing to go back to, so "throw it
   *  away and start again" is the only honest destructive action to offer. Once
   *  published, the draft is a pending edit to something live and Discard is the
   *  right control instead. */
  get canResetToSkeleton(): boolean {
    return this.workflow !== null && !this.workflow.published_at;
  }

  /** Warnings grouped by element id — feeds the RED canvas indicators (§7). */
  get warningsByElement(): Map<string, TWorkflowValidationWarning[]> {
    const map = new Map<string, TWorkflowValidationWarning[]>();
    for (const warning of this.workflow?.validation.warnings ?? []) {
      const list = map.get(warning.element_id);
      if (list) list.push(warning);
      else map.set(warning.element_id, [warning]);
    }
    return map;
  }

  /** Adopt the envelope + flat elements from a read/save response.
   *
   *  The elements are re-seated with STRUCTURAL SHARING ({@link reconcileElements}):
   *  the quiet autosave echo of an unchanged draft is byte-for-byte what we just
   *  PUT, so every element reconciles back to its existing object reference (and
   *  the array to its existing reference). The config panel derives its selected
   *  element from ``elements.find(...)`` — churning those references on every
   *  quiet save re-rendered the panel and COLLAPSED a Radix dropdown the author
   *  had open mid-edit (or dropped the in-progress pick), forcing them to wait out
   *  each ~1s debounce before touching the next control (#3758 part 2). An element
   *  that genuinely changed (BE-normalized position, the seeded first-Page id,
   *  refreshed content) still gets the fresh reference, so real changes propagate. */
  private seatFromResponse(workflow: TFormWorkflow): void {
    this.workflow = workflow;
    this.elements = this.reconcileElements(
      orderWorkflowElements(workflow.elements.map(normalizeElement)),
    );
    this.lastSavedIso = workflow.draft_updated_at ?? workflow.updated_at;
    this.hasUnpublishedChanges = workflow.has_unpublished_changes;
  }

  /** Reconcile a freshly-normalized element array against the current working
   *  copy, reusing an existing element's object reference wherever the incoming
   *  one is deep-equal, and reusing the current array reference when nothing moved
   *  or changed. This keeps object identity stable across a no-op save echo so
   *  downstream ``useMemo`` / ``observer`` derivations (the open config panel)
   *  don't re-render on a save that changed nothing. See {@link seatFromResponse}. */
  private reconcileElements(next: TWorkflowElement[]): TWorkflowElement[] {
    const currentById = new Map(this.elements.map((el) => [el.id, el]));
    const reconciled = next.map((incoming) => {
      const current = currentById.get(incoming.id);
      return current && deepEqual(current, incoming) ? current : incoming;
    });
    const identical =
      reconciled.length === this.elements.length &&
      reconciled.every((el, index) => el === this.elements[index]);
    return identical ? this.elements : reconciled;
  }

  /** Adopt only the server-owned meta from a response, keeping local
   *  ``elements`` (used when an edit landed mid-PUT — local edits with stable
   *  client ids stay referentially valid; the trailing save reconciles). */
  private seatMetaFromResponse(workflow: TFormWorkflow): void {
    this.workflow = workflow;
    this.lastSavedIso = workflow.draft_updated_at ?? workflow.updated_at;
    this.hasUnpublishedChanges = workflow.has_unpublished_changes;
  }

  /** A load of the draft has started: the editor shows its loading state. */
  beginLoad(): void {
    this.isLoading = true;
    this.loadError = null;
    this.loadErrorStatus = null;
  }

  /**
   * Seat a freshly read draft and its lookups (`null` when they failed) as the
   * editor's working copy, at the end of the draft and lookups queries
   * (`useFormWorkflowDraftLoad`, NG-2809). Only an answer read for this open may
   * be seated, never a reused one: the working copy is what autosave PUTs back.
   */
  seatLoaded(workflow: TFormWorkflow, lookups: TWorkflowLookups | null): void {
    this.seatFromResponse(workflow);
    this.lookups = lookups;
    this.isDirty = false;
    this.isLoading = false;
  }

  /** The draft could not be read: the editor shows the load error. */
  failLoad(slug: string, formId: string, err: unknown): void {
    const status = (err as { response?: { status?: number } })?.response?.status;
    console.error("form-workflow: load failed", { slug, formId, status, err });
    this.loadError = "load_failed";
    this.loadErrorStatus = typeof status === "number" ? status : null;
    this.isLoading = false;
  }

  /** Start a builder Preview walk (#2551) — the first step of a simulated
   *  requester path through the LIVE draft graph. Read-only: it does not seat or
   *  mutate the builder ``elements`` (safe to call from any tab, e.g. the header
   *  Preview button, whether or not the builder draft is loaded). The caller owns
   *  the loading / empty / error UI and the walk's subsequent steps (via
   *  {@link advancePreviewPage} / {@link advancePreviewReview}); this layer only
   *  surfaces the step and logs + rethrows on failure per CLAUDE.md.
   *
   *  Ordered behind the pending save so the walk reflects the graph on screen
   *  rather than the last debounced save (#2526); ``canSave`` is the NG-409 guard —
   *  both are {@link flushBeforeRead}. */
  async loadPreview(
    slug: string,
    formId: string,
    startElementId?: string,
    options: { canSave?: boolean } = {},
  ): Promise<TWorkflowPreviewStep> {
    await this.flushBeforeRead(slug, formId, "preview", startElementId, options);
    try {
      return await this.rootStore.formWorkflowService.loadPreview(slug, formId, startElementId);
    } catch (err) {
      console.error("form-workflow: preview load failed", { slug, formId, startElementId, err });
      throw err;
    }
  }

  /**
   * Order a draft read behind the pending autosave, best-effort.
   *
   * Every read below is answered from PERSISTED rows, while the author's edits
   * live in ``elements`` for up to one debounce window (#2526 / NG-81 / NG-878).
   * That gives the reads two failure modes, and both are permanent because each
   * caller keys its effect on the element id alone and so never refetches:
   *
   *  - **Stale.** Preview walks the last-saved graph instead of the one on screen.
   *  - **Absent.** An element id is CLIENT-generated, so a node the author just
   *    added is unknown server-side — the endpoint raises
   *    ``WorkflowStep.DoesNotExist`` and the 404 degrades to a plausible-looking
   *    wrong answer: "This page is no longer available.", an empty condition-source
   *    list, a Status that reports itself un-inherited.
   *
   * So the ordering is an invariant of the READ, owned here, rather than something
   * each call site has to remember — three of them forgot, one per bug above.
   *
   * ``canSave: false`` skips the flush entirely (NG-409). These are reads on
   * ``form_workflow.read``, which a view-only holder has; the draft PUT is gated on
   * ``.edit``, which they do not. Read-only disarms the autosave EFFECT, but a store
   * that goes dirty by any other route (hydration normalization, a control that
   * slipped the ``readOnly`` blanket) would still reach a forbidden PUT through
   * here — a 403 the reader cannot act on, in front of a surface that renders
   * anyway. The caller that knows whether the draft is writable says so; the
   * default stays the editor's behaviour so no existing call site changes meaning.
   *
   * Never throws: a failed flush still lets the read (and its own error handling)
   * proceed against the last-persisted draft.
   */
  private async flushBeforeRead(
    slug: string,
    formId: string,
    operation: string,
    elementId: string | undefined,
    options: { canSave?: boolean },
  ): Promise<void> {
    if (options.canSave === false) return;
    try {
      await this.flushPendingSave(slug, formId);
    } catch (err) {
      console.warn(`form-workflow: flush before ${operation} failed; the read may be stale`, {
        slug,
        formId,
        elementId,
        err,
      });
    }
  }

  /** Reconstruct one Page's current elements as a legacy-``definition`` page —
   *  what the Edit Page modal hydrates its tree from on open (#2271). Ordered
   *  behind the pending save (NG-878), see {@link flushBeforeRead}; rethrows the
   *  read's own failure for the caller. */
  async loadPageDefinition(
    slug: string,
    formId: string,
    pageId: string,
    options: { canSave?: boolean } = {},
  ): Promise<{ page: TIntakePage }> {
    await this.flushBeforeRead(slug, formId, "page-definition", pageId, options);
    return this.rootStore.formWorkflowService.loadPageDefinition(slug, formId, pageId);
  }

  /** The value-bearing Page fields that unconditionally PRECEDE an element on the
   *  graph — the valid ``visible_if`` / Branch condition sources (#2356). Ordered
   *  behind the pending save (NG-81), see {@link flushBeforeRead}; rethrows so the
   *  caller owns the degrade. */
  async loadAvailableFields(
    slug: string,
    formId: string,
    elementId: string,
    options: { canSave?: boolean } = {},
  ): Promise<{ fields: TWorkflowAvailableField[] }> {
    await this.flushBeforeRead(slug, formId, "available-fields", elementId, options);
    return this.rootStore.formWorkflowService.loadAvailableFields(slug, formId, elementId);
  }

  /** Whether a step's Status is inherited from the Review decision option whose
   *  lane it opens, so the Status control can pre-fill + lock rather than invite an
   *  author to set a value the graph will override (#2406, extended to Review + End
   *  steps in #2956). Ordered behind the pending save (NG-878), see
   *  {@link flushBeforeRead} — note its degrade is a PLAUSIBLE answer here, which is
   *  why the ordering matters more than for the others. Rethrows so the caller owns
   *  the degrade. */
  async loadStatusSource(
    slug: string,
    formId: string,
    elementId: string,
    options: { canSave?: boolean } = {},
  ): Promise<TWorkflowStatusSource> {
    await this.flushBeforeRead(slug, formId, "status-source", elementId, options);
    return this.rootStore.formWorkflowService.loadStatusSource(slug, formId, elementId);
  }

  /** Submit the current Preview step's Page answers and advance the walk
   *  (#2551). ``surveyData`` is SurveyJS's collected data, keyed by the
   *  reconstructed definition's field names; {@link fieldValuesFromSurveyData}
   *  remaps a ``system_field`` element's name (its code) back to its element id
   *  — the BE's ``field_values`` contract — before posting. Throws (the caller
   *  keeps rendering the current step and surfaces the error, e.g. a 400 for an
   *  invalid field). */
  async advancePreviewPage(
    slug: string,
    formId: string,
    step: TWorkflowPreviewStep,
    surveyData: Record<string, unknown>,
  ): Promise<TWorkflowPreviewStep> {
    if (!step.current_element_id || !step.page) {
      throw new Error("advancePreviewPage called without a current Page step");
    }
    try {
      const fieldValues = fieldValuesFromSurveyData(step.page, surveyData);
      return await this.rootStore.formWorkflowService.advancePreview(slug, formId, {
        current_element_id: step.current_element_id,
        response_data: step.response_data,
        field_values: fieldValues,
      });
    } catch (err) {
      console.error("form-workflow: preview advance (page) failed", { slug, formId, err });
      throw err;
    }
  }

  /** Pick a decision option on a paused ``review_choice`` Preview step and
   *  advance the walk down that option's lane (#2551). */
  async advancePreviewReview(
    slug: string,
    formId: string,
    step: TWorkflowPreviewStep,
    decisionOptionId: string,
  ): Promise<TWorkflowPreviewStep> {
    if (!step.current_element_id) {
      throw new Error("advancePreviewReview called without a current Review step");
    }
    try {
      return await this.rootStore.formWorkflowService.advancePreview(slug, formId, {
        current_element_id: step.current_element_id,
        response_data: step.response_data,
        decision_option_id: decisionOptionId,
      });
    } catch (err) {
      console.error("form-workflow: preview advance (review) failed", { slug, formId, err });
      throw err;
    }
  }

  /** Persist any pending draft edits before a read or publish that must
   *  reflect them (builder Preview #2526, Publish). Awaits an autosave already
   *  in flight, then — if edits are still dirty (an edit made inside the
   *  autosave debounce window, before the debounced ``save`` fired) — issues
   *  one immediately. Calling ``save`` flips ``isDirty`` off, which the
   *  workflow editor's autosave effect observes and uses to clear its
   *  still-pending debounce timer, so no redundant trailing PUT follows.
   *  Throws on failure. Every draft READ goes through {@link flushBeforeRead},
   *  which degrades rather than propagate. The two callers that must NOT proceed
   *  on a failed flush call this directly and handle the throw — Publish, and
   *  leaving the builder tab (whose unmount would otherwise discard the edit).
   *  Both stop and toast rather than continue; see ``forms-edit.tsx``.
   *
   *  Concurrent callers collapse to ONE PUT — opening the Edit Page modal is two
   *  of them, from sibling effects in the same commit — and that rests on
   *  {@link performSave} clearing ``isDirty`` synchronously; see the note there. */
  async flushPendingSave(slug: string, formId: string): Promise<void> {
    if (this.saveInFlight) await this.saveInFlight;
    if (this.isDirty) await this.save(slug, formId);
  }

  /** Debounced-autosave entry point. Tracks the in-flight promise (so
   *  {@link flushPendingSave} can await it) and delegates to {@link performSave}. */
  async save(slug: string, formId: string): Promise<void> {
    const run = this.performSave(slug, formId);
    this.saveInFlight = run;
    try {
      await run;
    } finally {
      if (this.saveInFlight === run) this.saveInFlight = null;
    }
  }

  private async performSave(slug: string, formId: string): Promise<void> {
    // The BE rejects an empty graph (the first Page is fixed); never PUT one.
    if (this.elements.length === 0) return;

    const name = this.workflow?.name ?? "";
    const payload = toSavePayload(name, this.elements);
    // What this save changes against the last stored document, for
    // `workflow_updated`: read now, before the response replaces `workflow`.
    const changes = workflowSaveChanges(
      this.workflow
        ? toSavePayload(this.workflow.name, this.workflow.elements.map(normalizeElement))
        : null,
      payload,
    );
    // Snapshot at ISSUE time: flip dirty off and record the version we're
    // saving. If a newer edit bumps ``dirtyVersion`` before the PUT resolves,
    // we must not clobber it with the server echo (the letter-drop bug).
    //
    // This flip must stay in the synchronous prefix, ahead of the ``await``
    // below: it is also the ONLY thing deduping concurrent ``flushPendingSave``
    // callers to a single PUT. The second flush either awaits the ``saveInFlight``
    // the first registered, or reaches its ``isDirty`` check after this line has
    // already cleared it. Move this behind an await and every concurrent caller
    // PUTs the whole document.
    const versionAtSave = this.dirtyVersion;
    runInAction(() => {
      this.isSaving = true;
      this.isDirty = false;
    });
    try {
      const next = await this.rootStore.formWorkflowService.saveWorkflow(slug, formId, payload);
      runInAction(() => {
        if (this.dirtyVersion === versionAtSave) {
          // No edit landed during the PUT — adopt the authoritative response
          // (BE-normalized positions, refreshed warnings, seeded first-Page id).
          this.seatFromResponse(next);
        } else {
          // An edit landed mid-PUT: keep local elements (stable client ids stay
          // valid) and adopt only server-owned meta. ``isDirty`` was re-set by
          // the edit, so the trailing debounced save reconciles.
          this.seatMetaFromResponse(next);
        }
      });
      if (changes.fields.length > 0) {
        track("workflow_updated", { form_id: formId, ...changes, source: "page" });
      }
    } catch (err) {
      runInAction(() => {
        // The issued snapshot never reached the server. If no newer edit/save
        // superseded it, restore dirty so navigation stays blocked and a retry
        // can persist the still-present local workflow.
        if (this.dirtyVersion === versionAtSave) this.isDirty = true;
      });
      console.error("form-workflow: autosave failed", { slug, formId, err });
      throw err;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  /** Persist one Page's full element set from an intake definition page (Edit Page
   *  modal, #2271). Routes through the BE convert endpoint (the shared #2202
   *  converter) and adopts the returned workflow envelope, so the canvas reflects
   *  the saved elements immediately. Throws on a 400 (unconvertible content,
   *  carrying ``blockers``) so the caller can surface which components it couldn't
   *  save; the layer never swallows the error.
   *
   *  The answer is the whole workflow as the server holds it, and it is adopted
   *  whole. The modal's Page settings (who fills it, status, notify, internal)
   *  are graph edits that wait on the debounced autosave, so one changed just
   *  before the close would be reverted by that answer, and the autosave would
   *  then store the reverted copy. Flush them first (NG-3198); a failed flush
   *  throws, so the caller reports it rather than reverting the edit. */
  async savePageFromDefinition(
    slug: string,
    formId: string,
    pageId: string,
    page: TIntakePage,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.flushPendingSave(slug, formId);
    const next = await this.rootStore.formWorkflowService.savePageDefinition(
      slug,
      formId,
      pageId,
      page,
    );
    runInAction(() => {
      // An author edit that deliberately does not dirty the draft (the backend
      // already saved it), so it cannot go through `markDirty` — but it is the
      // only path that adds or removes a page field or edits a visibility rule,
      // i.e. exactly the edits that fix the `missing_system_field` and
      // `visible_if_*` blockers.
      //
      // Conditional, because the Edit Page modal persists on EVERY close
      // (`edit-page-modal` awaits `onSave` whether or not anything changed), so
      // an unconditional bump would withdraw the banner's currency claim just
      // for opening a Page and closing it again. `reconcileElements` already
      // answers "did this actually change" by handing back the same array when
      // it didn't, so reuse that rather than trusting the modal.
      const before = this.elements;
      this.seatFromResponse(next);
      if (this.elements !== before) this.graphVersion += 1;
      // The same "did this actually change" answer decides the event: the modal
      // saves on every close, and a close that changed nothing is not an edit.
      if (this.elements !== before) {
        track("workflow_page_updated", { form_id: formId, page_id: pageId, source });
      }
    });
  }

  private markDirty(): void {
    this.dirtyVersion += 1;
    this.graphVersion += 1;
    this.isDirty = true;
  }

  renameElement(id: string, name: string): void {
    this.elements = this.elements.map((el) => (el.id === id ? { ...el, name } : el));
    this.markDirty();
  }

  /** The fixed first Page can never be deleted (PRD §6.1.4). */
  canDeleteElement(id: string): boolean {
    const el = this.elements.find((e) => e.id === id);
    if (!el) return false;
    return !(el.kind === "page" && el.page?.is_first);
  }

  /** Delete an element and its nested subtree (no orphan ``parent_id``). */
  deleteElement(id: string): void {
    if (!this.canDeleteElement(id)) return;
    // The same cascade the delete dialog's "Being removed" list is built from.
    const toRemove = workflowSubtreeIds(this.elements, id);
    this.elements = this.elements.filter((el) => !toRemove.has(el.id));
    this.markDirty();
  }

  /** Delete a Review or Branch but keep the steps on its ``keptLaneKey`` lane:
   *  they move up into the node's place and every other lane goes with the node
   *  (NG-2511). The delete dialog's "Staying" list is that lane's subtree, so the
   *  two must keep reading the same lane definition (``workflow-step-deletion``). */
  deleteElementKeepingLane(id: string, keptLaneKey: string): void {
    if (!this.canDeleteElement(id)) return;
    this.elements = orderWorkflowElements(deleteKeepingLane(this.elements, id, keptLaneKey));
    this.markDirty();
  }

  /** Insert a new element immediately after ``afterId`` on the SAME lane. Client
   *  UUID, empty subtype object; ``parent_id``/``branch_key`` are inherited from
   *  the ``afterId`` sibling so appending at a Review-lane / Branch-path tail
   *  stays on that lane rather than jumping to the top-level path (M4 #2093). A
   *  top-level sibling inherits ``(null, null)`` as before. */
  insertElementAfter(
    afterId: string,
    kind: TWorkflowElementKind,
    name: string,
    laneEndName?: string,
  ): void {
    const index = this.elements.findIndex((el) => el.id === afterId);
    if (index < 0) return;
    const after = this.elements[index];
    // An End is terminal (#2225): nothing may be added after it. The canvas
    // shouldn't offer a "+" past an End, but guard here too so no code path can
    // append a step to a terminated chain.
    if (after.kind === "end") {
      console.warn("form-workflow: refused to insert a step after a terminal End", {
        afterId,
        kind,
      });
      return;
    }
    const element: TWorkflowElement = {
      id: newWorkflowId(),
      kind,
      // ``position`` is normalised by ``renumberLanePositions`` below from the
      // spliced array order — don't hand it a global-index value here (that
      // collided with siblings and sorted the new step to the wrong place, e.g.
      // past a terminal End).
      name,
      position: 0,
      parent_id: after.parent_id,
      branch_key: after.branch_key,
      page: kind === "page" ? emptyPage() : null,
      review: kind === "review" ? emptyReview() : null,
      branch: kind === "branch" ? emptyBranch() : null,
      change_owner: kind === "change_owner" ? emptyChangeOwner() : null,
      end: kind === "end" ? emptyEnd() : null,
    };

    // Inserting a BRANCHING node (Branch / Review) between two steps re-parents the
    // downstream continuation on ``after``'s lane into the new node's FIRST outcome
    // — a Branch's "If true" path or a Review's Accept option — so the split lands
    // on the primary path (#2557). This intentionally restores the auto-routing
    // #2466 removed, but ONLY for branching inserts: a plain Page insert leaves the
    // successor linear on the same lane (``outcomeKey`` is null). The new node is
    // spliced right after ``after``; ``renumberLanePositions`` then densely orders
    // both ``after``'s lane and the new outcome lane from the array order.
    const outcomeKey = firstOutcomeKey(element);
    const next = this.elements.map((el) =>
      outcomeKey !== null &&
      el.parent_id === after.parent_id &&
      el.branch_key === after.branch_key &&
      el.position > after.position
        ? { ...el, parent_id: element.id, branch_key: outcomeKey }
        : el,
    );
    next.splice(index + 1, 0, element);
    this.elements = this.renumberLanePositions(this.seedReviewLaneEnds(next, element, laneEndName));
    this.markDirty();
  }

  /** Seed a terminal End at the open end of each of a freshly inserted Review's
   *  decision lanes (#2955), so every branch terminates without the author
   *  hand-adding an End to each one. A lane already ending in an End is left
   *  alone; one ending in a Review/Branch routes onward via its own lanes, so it
   *  gets none here — only an EMPTY lane or one ending in a Page / Change Owner
   *  (an open linear tail) is terminated. No-op for a non-Review insert. The End
   *  is pushed at the array tail so ``renumberLanePositions`` orders it last
   *  within its lane (#3643). */
  private seedReviewLaneEnds(
    elements: TWorkflowElement[],
    review: TWorkflowElement,
    endName?: string,
  ): TWorkflowElement[] {
    if (review.kind !== "review" || !review.review) return elements;
    const result = [...elements];
    for (const option of review.review.decision_options) {
      // "Last" by ARRAY order — the same order ``renumberLanePositions`` uses —
      // so the check is consistent with how the lane is finally ordered.
      const lane = result.filter((el) => el.parent_id === review.id && el.branch_key === option.id);
      const last = lane.at(-1);
      if (last && !isOpenLinearWorkflowTailKind(last.kind)) continue;
      result.push({
        id: newWorkflowId(),
        kind: "end",
        name: endName ?? "End",
        position: 0,
        parent_id: review.id,
        branch_key: option.id,
        page: null,
        review: null,
        branch: null,
        change_owner: null,
        end: emptyEnd(),
      });
    }
    return result;
  }

  /** Reassign every element's ``position`` densely (0, 1, 2 …) within its lane
   *  (``parent_id`` + ``branch_key``) by ARRAY ORDER, so the order the canvas
   *  sorts siblings by matches the intended array order after a structural edit.
   *  ``insertElementAfter`` splices the new element right after its sibling, but
   *  a per-element ``position`` set from a global index could collide with a
   *  sibling's (e.g. the seeded End) and sort the new step to the wrong place —
   *  notably PAST a terminal End. The BE still renumbers densely per lane on
   *  save; this just keeps the LOCAL draft's ordering self-consistent. */
  private renumberLanePositions(elements: TWorkflowElement[]): TWorkflowElement[] {
    const nextByLane = new Map<string, number>();
    return elements.map((el) => {
      const laneKey = `${el.parent_id ?? ""}::${el.branch_key ?? ""}`;
      const pos = nextByLane.get(laneKey) ?? 0;
      nextByLane.set(laneKey, pos + 1);
      return el.position === pos ? el : { ...el, position: pos };
    });
  }

  /** Insert a new element at the HEAD of a lane — a Review decision option's
   *  lane (``branchKey`` = option id) or a Branch "If true" / "If false" path
   *  (``branchKey`` = ``"true"`` / ``"false"``). Backs the "+" affordance at an
   *  open (empty) lane end AND the lane-start "+" that injects a step before a
   *  non-empty lane's first stage (M4 #2093, §6.2.2 / §6.2.3). No-op if the
   *  parent is gone. Placed right after the parent in ``elements`` so array order
   *  stays readable; the BE renumbers ``position`` densely per lane on save. */
  insertLaneStart(
    parentId: string,
    branchKey: string,
    kind: TWorkflowElementKind,
    name: string,
    laneEndName?: string,
  ): void {
    const parentIndex = this.elements.findIndex((el) => el.id === parentId);
    if (parentIndex < 0) return;
    const element: TWorkflowElement = {
      id: newWorkflowId(),
      kind,
      name,
      position: 0,
      parent_id: parentId,
      branch_key: branchKey,
      page: kind === "page" ? emptyPage() : null,
      review: kind === "review" ? emptyReview() : null,
      branch: kind === "branch" ? emptyBranch() : null,
      change_owner: kind === "change_owner" ? emptyChangeOwner() : null,
      end: kind === "end" ? emptyEnd() : null,
    };

    // Inserting a BRANCHING node at the lane HEAD re-parents the lane's existing
    // content into the new node's FIRST outcome (Branch "If true" / Review Accept),
    // so the existing continuation lands on the primary path (#2557). Restores the
    // auto-routing #2466 removed, for branching inserts only; a Page insert leaves
    // the existing content linear on the lane (``outcomeKey`` is null).
    const outcomeKey = firstOutcomeKey(element);
    const next = this.elements.map((el) =>
      outcomeKey !== null && el.parent_id === parentId && el.branch_key === branchKey
        ? { ...el, parent_id: element.id, branch_key: outcomeKey }
        : el,
    );
    next.splice(parentIndex + 1, 0, element);
    this.elements = this.renumberLanePositions(this.seedReviewLaneEnds(next, element, laneEndName));
    this.markDirty();
  }

  /** Move an existing step onto another lane — the kebab "Move to…" action
   *  (#2435). The step's own subtree (its Review/Branch lanes and children
   *  reference its id) follows implicitly, so a large downstream chain relocates
   *  intact — the whole point: delete-and-re-add would destroy it. The step
   *  lands at the destination lane's tail, before a trailing End if the lane has
   *  one (an End is terminal, #2338). Refused (warn, no-op): the fixed first
   *  Page, a destination inside the step's own subtree (cycle), and moving an
   *  End onto a lane that already has one. */
  /**
   * Relocate a step onto another lane via the "Move to" menu — the alternate
   * path to drag-and-drop (#2435). It lands at the destination lane's tail,
   * before a trailing End.
   *
   * Legality is decided by the SAME rule as a drop
   * ({@link resolveWorkflowLaneMove}), so the menu can no longer produce a graph
   * the drag would refuse: it used to splice a step in front of a trailing End
   * unconditionally, which stranded that End behind a moved Review or Branch on
   * a path no request can reach (NG-694). ``moveTargetsFor`` filters the menu on
   * the same predicate, so an illegal lane is never offered in the first place.
   */
  moveElementToLane(id: string, parentId: string | null, branchKey: string | null): void {
    const target = resolveWorkflowLaneMove(this.elements, id, parentId, branchKey);
    if (!target) {
      console.warn("form-workflow: refused an illegal Move-to", { id, parentId, branchKey });
      return;
    }
    this.applyMove(id, target);
  }

  /**
   * Move a step onto a canvas drop SLOT — a "+" insertion point — landing it at
   * that exact position rather than at the destination lane's tail (NG-694).
   * This is the drag-and-drop path; {@link moveElementToLane} remains the "Move
   * to" menu's append-to-lane path.
   *
   * Legality is decided once, by the pure {@link resolveWorkflowDrop}, so the
   * drag hook can light a slot up with the same predicate that commits here and
   * the two cannot disagree. An illegal or no-op drop is silently ignored — the
   * hook never offers one, so reaching this branch means a stale slot, not user
   * error.
   *
   */
  moveElementToSlot(id: string, slot: TWorkflowDropSlot): void {
    const target = resolveWorkflowDrop(this.elements, id, slot);
    if (!target) return;
    this.applyMove(id, target);
  }

  /**
   * Splice an already-VALIDATED move into ``elements``. Shared by the drag and
   * the "Move to" menu so the two cannot drift on how a move is applied; every
   * legality question is settled before this is called.
   *
   * The step's payload and subtree ride along untouched: children are keyed by
   * ``parent_id``, not by array adjacency, so only the moved row is spliced.
   */
  private applyMove(id: string, target: TWorkflowDropTarget): void {
    const element = this.elements.find((el) => el.id === id);
    if (!element) return;

    const { parentId, branchKey, index } = target;
    const next = this.elements.filter((el) => el.id !== id);
    const moved = { ...element, parent_id: parentId, branch_key: branchKey };
    // ``index`` counts the destination lane WITHOUT the dragged step, which is
    // exactly what ``next`` holds — so the lane member at ``index`` is the row to
    // splice in front of, and a tail drop goes after the last one.
    const lane = next.filter((el) => el.parent_id === parentId && el.branch_key === branchKey);
    if (index < lane.length) {
      next.splice(
        next.findIndex((el) => el.id === lane[index].id),
        0,
        moved,
      );
    } else if (lane.length > 0) {
      next.splice(next.findIndex((el) => el.id === lane[lane.length - 1].id) + 1, 0, moved);
    } else if (parentId !== null) {
      const parentIndex = next.findIndex((el) => el.id === parentId);
      if (parentIndex < 0) return;
      next.splice(parentIndex + 1, 0, moved);
    } else {
      next.push(moved);
    }
    this.elements = this.renumberLanePositions(next);
    this.markDirty();
  }

  /**
   * Replace a Page's content with the elements the modal builder produced.
   *
   * [#2154] page content becomes the intake element tree once Dan delivers the
   * schema; until then this writes the bank-field reference list shape
   * (``TWorkflowPageField[]``). The modal calls this on save; it bumps
   * ``dirtyVersion`` so the autosave effect PUTs the whole document (§6).
   */
  setPageElements(pageId: string, fields: TWorkflowPageField[]): void {
    this.elements = this.elements.map((el) =>
      el.id === pageId && el.kind === "page" && el.page
        ? { ...el, page: { ...el.page, fields } }
        : el,
    );
    this.markDirty();
  }

  // ── Config-panel mutations (M3 #2091) ─────────────────────────────────────
  // Each mirrors ``setPageElements`` / ``renameElement``: an immutable map over
  // ``elements`` guarded by ``id === id && kind === '<k>' && el.<k>`` so a
  // mismatched target is a no-op, then ``markDirty()`` so the autosave PUTs the
  // whole document and the BE re-computes warnings. The store is a DUMB setter:
  // the Accept/Deny-undeletable + ≥1-required rules live in the panels (UI), not
  // here — the BE is the validator.

  /** Page "Available teams" (2+ Pages only) — the teams whose members may fill
   *  this Page. Independent of {@link setPageAssigneeUsers}; both multi-selects
   *  are shown simultaneously (#2696). */
  setPageAssigneeTeams(id: string, teamIds: string[]): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "page" && el.page
        ? { ...el, page: { ...el.page, assignee_team_ids: teamIds } }
        : el,
    );
    this.markDirty();
  }

  /** Page "Available users" (2+ Pages only) — see {@link setPageAssigneeTeams}. */
  setPageAssigneeUsers(id: string, userIds: string[]): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "page" && el.page
        ? { ...el, page: { ...el.page, assignee_user_ids: userIds } }
        : el,
    );
    this.markDirty();
  }

  setPageStatus(id: string, statusId: string | null): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "page" && el.page
        ? { ...el, page: { ...el.page, status_id: statusId } }
        : el,
    );
    this.markDirty();
  }

  /** Page "Send notification" toggle (#2699 S2) — when true the runtime notifies
   *  the page's audience as the request lands on this page (S3 #2719). Mirrors
   *  {@link setPageStatus}: a dumb immutable setter, the BE stays the validator. */
  setPageNotify(id: string, notify: boolean): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "page" && el.page
        ? { ...el, page: { ...el.page, notify_on_arrival: notify } }
        : el,
    );
    this.markDirty();
  }

  /** The author-written body sent with the page-arrival notification (#2951),
   *  mirroring {@link setReviewNotification}. Only surfaced when notify is on. */
  setPageNotificationMessage(id: string, message: string): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "page" && el.page
        ? { ...el, page: { ...el.page, notification_message: message } }
        : el,
    );
    this.markDirty();
  }

  /** Mirror the Page's "Internal page" flag onto the workflow PageStep so the
   *  runtime can route no-assignee default fillers (issue 2954). Driven by the
   *  same toggle that sets the definition page's ``internal`` (one switch). */
  setPageInternal(id: string, internal: boolean): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "page" && el.page
        ? { ...el, page: { ...el.page, internal } }
        : el,
    );
    this.markDirty();
  }

  /** Remove one field from a Page's read-only field list (the panel's only field
   *  mutation; add/reorder stay in the Edit Page modal). */
  setReviewStatus(id: string, statusId: string | null): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "review" && el.review
        ? { ...el, review: { ...el.review, status_id: statusId } }
        : el,
    );
    this.markDirty();
  }

  setReviewers(id: string, userIds: string[]): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "review" && el.review
        ? { ...el, review: { ...el.review, reviewer_ids: userIds } }
        : el,
    );
    this.markDirty();
  }

  setReviewNotification(id: string, message: string): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "review" && el.review
        ? { ...el, review: { ...el.review, notification_message: message } }
        : el,
    );
    this.markDirty();
  }

  /** Replace the decision-option array wholesale (the panel owns the add / rename
   *  / remove / status / re-position math). */
  setDecisionOptions(id: string, options: TWorkflowDecisionOption[]): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "review" && el.review
        ? { ...el, review: { ...el.review, decision_options: options } }
        : el,
    );
    this.markDirty();
  }

  /** Remove one decision option from a Review AND cascade-delete its lane
   *  subtree in a single edit, then re-densify the remaining options'
   *  ``position``. Unlike {@link setDecisionOptions} (the panel's wholesale
   *  add/rename/status/re-position setter), removing an option must also drop
   *  the stages configured under its lane so they aren't orphaned with a
   *  dangling ``branch_key`` (§6.2.2 / §7). The panel owns the Accept/Deny
   *  undeletable rule and the remove-option box the confirmation (NG-2649); this
   *  is the atomic mutation. It removes the lane's Ends too, which the box leaves
   *  out of its list (``laneSubtreeSteps``): both read the lane through
   *  ``laneSubtreeIds``, so keep them on it. */
  removeDecisionOption(reviewId: string, optionId: string): void {
    const laneIds = laneSubtreeIds(this.elements, reviewId, optionId);
    this.elements = this.elements
      .filter((el) => !laneIds.has(el.id))
      .map((el) =>
        el.id === reviewId && el.kind === "review" && el.review
          ? {
              ...el,
              review: {
                ...el.review,
                decision_options: el.review.decision_options
                  .filter((o) => o.id !== optionId)
                  .map((o, index) => ({ ...o, position: index })),
              },
            }
          : el,
      );
    this.markDirty();
  }

  /** Remove a decision option but keep the steps on its path: they move to the
   *  tail of ``targetOptionId``'s path, before its End, and only the option and
   *  its path's End go (NG-2649). Legality is ``canMoveLaneSteps``, the same rule
   *  the remove-option box offers paths by; a refused move changes nothing. */
  removeDecisionOptionMovingSteps(
    reviewId: string,
    optionId: string,
    targetOptionId: string,
  ): void {
    const moved = moveLaneSteps(this.elements, reviewId, optionId, targetOptionId);
    // ``moveLaneSteps`` hands back the same array when it refuses the move.
    if (moved === this.elements) {
      console.warn("form-workflow: refused an illegal move of a decision option's steps", {
        reviewId,
        optionId,
        targetOptionId,
      });
      return;
    }
    this.elements = orderWorkflowElements(moved);
    this.removeDecisionOption(reviewId, optionId);
  }

  setBranchTrueStatus(id: string, statusId: string | null): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "branch" && el.branch
        ? { ...el, branch: { ...el.branch, true_status_id: statusId } }
        : el,
    );
    this.markDirty();
  }

  setBranchFalseStatus(id: string, statusId: string | null): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "branch" && el.branch
        ? { ...el, branch: { ...el.branch, false_status_id: statusId } }
        : el,
    );
    this.markDirty();
  }

  /** Write ``branch.logic`` + ``branch.conditions`` together (the Branch panel
   *  maps M4's ``{logic, conditions[]}`` into the positioned wire shape). */
  setBranchConditions(
    id: string,
    logic: TWorkflowBranchLogic,
    conditions: TWorkflowBranchCondition[],
  ): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "branch" && el.branch
        ? { ...el, branch: { ...el.branch, logic, conditions } }
        : el,
    );
    this.markDirty();
  }

  /** Replace a Change Owner node's whole payload (#3643). The panel edits mode,
   *  source, rules and targets together — a single write keeps the "static has
   *  exactly one always-matching rule" invariant from ever being briefly false. */
  setChangeOwner(id: string, next: NonNullable<TWorkflowElement["change_owner"]>): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "change_owner" ? { ...el, change_owner: next } : el,
    );
    this.markDirty();
  }

  /**
   * Flip an End between auto-create and manual-queue completion.
   *
   * Switching back to ``manual`` clears any bound project template (#4154):
   * leaving a stale binding on a manual End would republish a snapshot carrying
   * a template nothing reads, and would silently resurrect the old selection if
   * the author flipped back to ``auto`` later.
   */
  setEndCompletion(id: string, completion: TWorkflowCompletion): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "end" && el.end
        ? {
            ...el,
            end: {
              ...el.end,
              completion,
              project_template_id: completion === "auto" ? el.end.project_template_id : null,
            },
          }
        : el,
    );
    this.markDirty();
  }

  setEndProjectTemplate(id: string, projectTemplateId: string | null): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "end" && el.end
        ? { ...el, end: { ...el.end, project_template_id: projectTemplateId } }
        : el,
    );
    this.markDirty();
  }

  setEndReopenDest(id: string, destId: string | null): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "end" && el.end
        ? { ...el, end: { ...el.end, reopen_dest_id: destId } }
        : el,
    );
    this.markDirty();
  }

  setEndStatus(id: string, statusId: string | null): void {
    this.elements = this.elements.map((el) =>
      el.id === id && el.kind === "end" && el.end
        ? { ...el, end: { ...el.end, status_id: statusId } }
        : el,
    );
    this.markDirty();
  }

  async publishWorkflow(
    slug: string,
    formId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    runInAction(() => {
      this.incompleteNodes = [];
    });
    // Snapshot BEFORE the round trip, the same way `performSave` snapshots
    // `dirtyVersion`: nothing disables the canvas while publish is in flight, so
    // an edit can land between issuing the request and handling its response.
    // Stamping the post-response value would record the report as current for a
    // graph it was not computed from.
    const versionAtPublish = this.graphVersion;
    try {
      const published = await this.rootStore.formWorkflowService.publish(slug, formId);
      runInAction(() => {
        this.seatFromResponse(published);
      });
      track("workflow_published", { form_id: formId, source });
    } catch (err) {
      const incomplete = parseIncompleteNodes(err);
      if (incomplete) {
        // A 422 with incomplete paths is an expected outcome (the banner, #2095,
        // surfaces it) — not an unexpected failure.
        console.error("form-workflow: publish blocked by validation", {
          slug,
          formId,
          incompleteNodes: incomplete,
          err,
        });
        runInAction(() => {
          this.incompleteNodes = incomplete;
          this.incompleteNodesAtGraphVersion = versionAtPublish;
        });
        return;
      }
      console.error("form-workflow: publish failed", { slug, formId, err });
      throw err;
    }
  }

  /** Throw the unpublished draft graph away and restore the last published one
   *  (NG-211).
   *
   *  Waits out a save already on the wire, but deliberately does NOT
   *  ``flushPendingSave``: the point is to discard those edits, so pushing them
   *  to the server first would be exactly backwards.
   *
   *  Clearing ``isDirty`` BEFORE the POST is what cancels the editor's pending
   *  debounce timer — its autosave effect keys on ``isDirty`` and clears the
   *  timeout in cleanup. Leave it set and the trailing autosave PUTs the
   *  discarded graph straight back a second later, which would look to the
   *  author exactly like the bug this fixes.
   *
   *  Rejects with the service error. A 409 means the workflow has never been
   *  published, so there is no version to revert to; the caller says so rather
   *  than showing a generic failure. */
  async discardWorkflow(
    slug: string,
    formId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    if (this.saveInFlight) {
      // A failing in-flight save is not a reason to refuse the discard — its
      // edits are being thrown away regardless, and blocking here would deny
      // the author the recovery action precisely when saving is unhealthy.
      await this.saveInFlight.catch((err: unknown) => {
        console.warn("form-workflow: in-flight save failed while discarding", {
          slug,
          formId,
          err,
        });
      });
    }
    runInAction(() => {
      this.isDirty = false;
      this.incompleteNodes = [];
    });
    try {
      const reverted = await this.rootStore.formWorkflowService.discard(slug, formId);
      runInAction(() => {
        this.seatFromResponse(reverted);
      });
      track("workflow_discarded", { form_id: formId, source });
    } catch (err) {
      console.error("form-workflow: discard failed", { slug, formId, err });
      throw err;
    }
  }

  /** Start a never-published workflow over from the seeded skeleton (NG-235).
   *
   *  Same in-flight handling as {@link discardWorkflow} and for the same
   *  reasons: wait out a save already on the wire, do NOT flush pending edits
   *  (they are what is being thrown away), and clear ``isDirty`` before the POST
   *  so the editor's debounce timer is cancelled rather than re-saving the graph
   *  we just destroyed. */
  async resetWorkflow(
    slug: string,
    formId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    if (this.saveInFlight) {
      await this.saveInFlight.catch((err: unknown) => {
        console.warn("form-workflow: in-flight save failed while resetting", { slug, formId, err });
      });
    }
    runInAction(() => {
      this.isDirty = false;
      this.incompleteNodes = [];
    });
    try {
      const fresh = await this.rootStore.formWorkflowService.reset(slug, formId);
      runInAction(() => {
        this.seatFromResponse(fresh);
      });
      track("workflow_reset", { form_id: formId, source });
    } catch (err) {
      console.error("form-workflow: reset failed", { slug, formId, err });
      throw err;
    }
  }

  /** Clear the incomplete-node blockers so the validation banner hides — the
   *  author dismissed it, or a fresh publish attempt is starting (#2204). */
  dismissIncompleteNodes(): void {
    this.incompleteNodes = [];
  }

  reset(): void {
    runInAction(() => {
      this.workflow = null;
      this.elements = [];
      this.lookups = null;
      this.isLoading = false;
      this.isSaving = false;
      this.loadError = null;
      this.loadErrorStatus = null;
      this.lastSavedIso = null;
      this.hasUnpublishedChanges = false;
      this.dirtyVersion = 0;
      this.isDirty = false;
      this.incompleteNodes = [];
      this.saveInFlight = null;
    });
  }
}

function emptyPage(): TWorkflowElement["page"] {
  return {
    is_first: false,
    // "Who fills this page" is now two independent multi-selects — available
    // teams + available users (#2696) — so a fresh Page opens with both empty
    // rather than a defaulted role. Status is deliberately left unset —
    // auto-picking one would change publish-validation semantics.
    assignee_team_ids: [],
    assignee_user_ids: [],
    status_id: null,
    // No post-submission notification by default (#2699 S1); the author opts in
    // via the Page-settings toggle (S2 #2718). The message body (#2951) starts
    // empty and is only editable once the toggle is on.
    notify_on_arrival: false,
    notification_message: "",
    // Not internal by default (#2954); the "Internal page" toggle sets it.
    internal: false,
    fields: [],
  };
}

/** The first-outcome lane key of a freshly built branching node: a Branch's
 *  "If true" path (``"true"``) or a Review's first decision option (Accept).
 *  ``null`` for a non-branching kind (a Page / End has no outcome lane), which is
 *  the signal that an insert must leave the downstream continuation linear.
 *  Read through ``outcomeLaneKeys`` so the delete dialog (NG-2511) offers this same
 *  lane first: keeping it undoes exactly what the insert did. */
function firstOutcomeKey(element: TWorkflowElement): string | null {
  return outcomeLaneKeys(element)[0] ?? null;
}

function emptyReview(): TWorkflowElement["review"] {
  // Seed the two mandatory, non-deletable Accept/Deny defaults (PRD §6.4.3, #2144)
  // up front with ``is_default: true`` + client-stable ids, so a new Review is
  // valid immediately and each default's branch lane (keyed by the option id)
  // round-trips across saves. Sending ``decision_options: []`` instead SUPPRESSED
  // the BE's absent-key seed (an explicit ``[]`` is a deliberate zero-option
  // Review), leaving every new Review with no defaults → the "missing
  // non-deletable Accept / Deny" warning always fired (#2326).
  return {
    status_id: null,
    notification_message: "",
    reviewer_ids: [],
    decision_options: [
      { id: newWorkflowId(), name: "Accept", status_id: null, is_default: true, position: 0 },
      { id: newWorkflowId(), name: "Deny", status_id: null, is_default: true, position: 1 },
    ],
  };
}

function emptyBranch(): TWorkflowElement["branch"] {
  return { logic: "and", true_status_id: null, false_status_id: null, conditions: [] };
}

/** A new Change Owner node (#3643) starts in ``static`` mode carrying its single
 *  always-matching rule, so the panel has a row to fill in immediately and the
 *  engine's "one resolution path" invariant holds from the first save. */
function emptyChangeOwner(): TWorkflowElement["change_owner"] {
  return {
    mode: "static",
    replace_current_owner: false,
    source_element_id: null,
    rules: [
      { id: newWorkflowId(), value: "", position: 0, target_user_ids: [], target_team_ids: [] },
    ],
  };
}

function emptyEnd(): TWorkflowElement["end"] {
  return {
    completion: "manual",
    reopen_dest_id: null,
    status_id: null,
    project_template_id: null,
  };
}

/** Coerce a BE-loaded element into the shape the editor consumers assume. The
 *  Page content contract is interim (#2154/#2088) and the BE may return a Page
 *  whose ``fields`` key is absent/null; consumers that iterate it (the Edit Page
 *  hydrate spread, ``removeField``, the node field count) assume an array, so
 *  normalize it to ``[]`` at ingest rather than guarding every reader (#2221). */
/** Re-order the flat ``elements`` into a canonical array order: every node before
 *  its own lane content (parent-first, depth-first), and within each lane
 *  (``parent_id`` + ``branch_key``) ascending by ``position`` with a terminal End
 *  last on a position tie.
 *
 *  The canvas renderer (``adaptToGraph``) sorts each lane by ``position`` and is
 *  ARRAY-order independent, but the insert mutators (``insertElementAfter`` /
 *  ``insertLaneStart``) splice the new step next to its anchor by ARRAY index and
 *  ``renumberLanePositions`` reassigns each lane's ``position`` by array-traversal
 *  order. The BE serializes ``elements`` ``order_by("parent_id", "position")`` with
 *  the top-level NULL-parent rows LAST — so a Review/Branch's lane children arrive
 *  BEFORE the parent, the parent's array index is near the tail, and an insert at
 *  ``parentIndex + 1`` renumbered the new step PAST a terminal End (the "step lands
 *  after End" bug, #2534). Normalizing on load makes array order faithfully mirror
 *  the intended per-lane order, so the splice logic is correct by construction —
 *  independent of whatever order the wire delivered. */
export function orderWorkflowElements(elements: TWorkflowElement[]): TWorkflowElement[] {
  const childrenByParent = new Map<string | null, TWorkflowElement[]>();
  for (const el of elements) {
    const bucket = childrenByParent.get(el.parent_id);
    if (bucket) bucket.push(el);
    else childrenByParent.set(el.parent_id, [el]);
  }

  const ordered: TWorkflowElement[] = [];
  const seen = new Set<string>();

  const visit = (parentId: string | null): void => {
    const kids = childrenByParent.get(parentId);
    if (!kids) return;
    // Group by lane (``branch_key``), preserving first-seen lane order — cross-lane
    // array order does not affect rendering (the adapter reads lane membership from
    // the parent's decision options / branch paths), only per-lane order matters.
    const laneOrder: (string | null)[] = [];
    const byLane = new Map<string | null, TWorkflowElement[]>();
    for (const el of kids) {
      const lane = byLane.get(el.branch_key);
      if (lane) lane.push(el);
      else {
        byLane.set(el.branch_key, [el]);
        laneOrder.push(el.branch_key);
      }
    }
    for (const branchKey of laneOrder) {
      byLane
        .get(branchKey)!
        // A terminal End sorts last on a position tie so an insert can never be
        // ranked past it; the original array index is the final stable tiebreak.
        .map((el, index) => ({ el, index }))
        .sort(
          (a, b) =>
            a.el.position - b.el.position ||
            (a.el.kind === "end" ? 1 : 0) - (b.el.kind === "end" ? 1 : 0) ||
            a.index - b.index,
        )
        .forEach(({ el }) => {
          if (seen.has(el.id)) return;
          seen.add(el.id);
          ordered.push(el);
          visit(el.id); // emit this node before descending into its own lanes
        });
    }
  };

  visit(null);
  // Preserve any orphan whose parent chain never reaches root (a stray node whose
  // parent was deleted) so it stays visible + deletable rather than vanishing.
  for (const el of elements) {
    if (!seen.has(el.id)) {
      seen.add(el.id);
      ordered.push(el);
    }
  }
  return ordered;
}

/** Structural (deep) equality over the plain JSON-shaped values the workflow
 *  wire carries (objects / arrays / primitives / null — no Dates, Maps, or
 *  functions). Key-order independent, so an element rebuilt by a client mutator
 *  (spread) compares equal to the same element re-parsed from the BE echo. Used
 *  by {@link FormWorkflowStore.reconcileElements} to reuse an unchanged element's
 *  object reference across a no-op save. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || a === null || typeof b !== "object" || b === null) return false;
  const aIsArray = Array.isArray(a);
  if (aIsArray !== Array.isArray(b)) return false;
  if (aIsArray) {
    const arrA = a as unknown[];
    const arrB = b as unknown[];
    if (arrA.length !== arrB.length) return false;
    return arrA.every((item, index) => deepEqual(item, arrB[index]));
  }
  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;
  const keysA = Object.keys(objA);
  if (keysA.length !== Object.keys(objB).length) return false;
  return keysA.every(
    (key) => Object.prototype.hasOwnProperty.call(objB, key) && deepEqual(objA[key], objB[key]),
  );
}

function normalizeElement(el: TWorkflowElement): TWorkflowElement {
  if (el.kind === "page" && el.page) {
    // ``fields`` is typed as a required array, but the wire payload can omit it
    // under the interim contract — widen to catch the real runtime value.
    const fields = el.page.fields as TWorkflowPageField[] | undefined;
    if (!Array.isArray(fields)) return { ...el, page: { ...el.page, fields: [] } };
  }
  return el;
}

/** Remap a Preview walk's collected SurveyJS data (keyed by the reconstructed
 *  definition's field names, {@link flattenPageFields}) to the BE's
 *  ``field_values`` contract, keyed by element id (#2551).
 *
 *  ``definition_render._field_name`` names a ``system_field`` element by its
 *  standard-field code and every other value-bearing element by its own
 *  element id — so this is only ever a real remap for a ``system_field``
 *  element; everything else's SurveyJS name already IS its element id. */
export function fieldValuesFromSurveyData(
  page: TWorkflowElement,
  surveyData: Record<string, unknown>,
): Record<string, unknown> {
  const nameToId = new Map<string, string>();
  for (const el of flattenPageFields(page.page?.elements)) {
    nameToId.set(el.kind === "system_field" ? el.system_field : el.id, el.id);
  }
  const fieldValues: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(surveyData)) {
    fieldValues[nameToId.get(name) ?? name] = value;
  }
  return fieldValues;
}

/** Pull ``incomplete_nodes`` out of a 422 publish error, or null for any other
 *  error (which the caller treats as unexpected and logs + rethrows). */
function parseIncompleteNodes(err: unknown): TPublishIncompleteNode[] | null {
  const response = (err as { response?: { status?: number; data?: unknown } })?.response;
  if (response?.status !== 422) return null;
  const data = response.data as { incomplete_nodes?: TPublishIncompleteNode[] } | undefined;
  return Array.isArray(data?.incomplete_nodes) ? data.incomplete_nodes : [];
}
